import { T } from '@tolgee/react';
import { Modal } from 'antd';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import { ChatSvg } from '../../../assets/icons/ChatSvg';
import { CuriLightbulb } from '../../../assets/icons/CuriLightbulb';
import { CuriOpenArms } from '../../../assets/icons/CuriOpenArms';
import { CuriScoreCard } from '../../../assets/icons/CuriScoreCard';
import { DownloadSvg } from '../../../assets/icons/DownloadSvg';
import { LogoSvg } from '../../../assets/icons/LogoSvg';
import theme from '../../../assets/theme';
import { useWindowSize } from '../../../hooks/useWindowSize';
import Metrics from '../../../services/metrics';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { resolveRegion, useFeaturePaywallContent } from './featurePaywallContent';

interface FeaturePaywallProps {
  visible: boolean;
  onClose: () => void;
  region?: string;
  country?: string;
}

const MainContainer = styled.div`
  width: 100%;
  background-color: ${theme.colors.paleBeige};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 40px 20px;
  box-sizing: border-box;
  gap: 14px;
  max-width: 1000px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 20px 20px 16px;
    gap: 12px;
  }
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Title = styled.h2`
  margin: 0;
  padding: 0;
  font-family: 'Nunito', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const SubText = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${theme.colors.subtitleGrey};
  margin: 0;
  max-width: 480px;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff;
  border-radius: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.black};
  width: 100%;
  max-width: 520px;
`;

const FeatureIconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(249, 151, 70, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.orange};

  svg {
    width: 30px;
    height: 30px;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 520px;
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterLink = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  color: ${theme.colors.orange};
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const FooterDivider = styled.span`
  color: ${theme.colors.grey};
  font-size: 14px;
`;

export const FeaturePaywall: FunctionComponent<FeaturePaywallProps> = ({
  visible,
  onClose,
  region,
  country,
}) => {
  const [windowWidth] = useWindowSize();
  const { showNewMessages } = useIntercom();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const paywallRegion = useMemo(() => resolveRegion(region, country), [region, country]);
  const content = useFeaturePaywallContent(paywallRegion);
  const isUS = paywallRegion !== 'INTL';

  const featureIcons = useMemo(
    () => [
      <CuriOpenArms key="openarms" />,
      <CuriScoreCard key="scorecard" />,
      <CuriLightbulb key="lightbulb" />,
    ],
    [],
  );

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width={windowWidth < 600 ? '95%' : 640}
      closable={true}
      maskClosable={true}
      className="feature-paywall"
      style={{ top: 20 }}
      bodyStyle={{
        padding: 0,
        backgroundColor: theme.colors.paleBeige,
        borderRadius: '10px',
      }}
    >
      <MainContainer>
        <HeaderGroup>
          <IconWrapper
            color={theme.colors.orange}
            height={'36px'}
            width={'130px'}
            cursor="default"
          >
            <LogoSvg />
          </IconWrapper>

          <Title>{content.title}</Title>
          <SubText>{content.subtitle}</SubText>
        </HeaderGroup>

        {content.features.map((feature, i) => (
          <FeatureRow key={i}>
            <FeatureIconCircle>{featureIcons[i] ?? featureIcons[0]}</FeatureIconCircle>
            {feature}
          </FeatureRow>
        ))}

        <CTAGroup>
          {isUS ? (
            <a
              href={content.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <Button
                type="orange"
                onClick={() => {
                  Metrics.getLogger().logEvent('FeaturePaywall.BookCall', {
                    region: paywallRegion,
                  });
                }}
                width="100%"
                styles={{
                  borderRadius: '24px',
                  maxWidth: 520,
                  padding: '12px 24px',
                  fontSize: 15,
                }}
              >
                {content.ctaText}
              </Button>
            </a>
          ) : (
            <a
              href={content.ctaLink}
              style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <Button
                type="orange"
                onClick={() => {
                  Metrics.getLogger().logEvent('FeaturePaywall.ViewPricing', {
                    region: paywallRegion,
                  });
                }}
                width="100%"
                styles={{
                  borderRadius: '24px',
                  maxWidth: 520,
                  padding: '12px 24px',
                  fontSize: 15,
                }}
              >
                {content.ctaText}
              </Button>
            </a>
          )}

          <FooterLinks>
            <FooterLink
              onClick={() => {
                Metrics.getLogger().logEvent('FeaturePaywall.ChatWithUs', {
                  region: paywallRegion,
                });
                showNewMessages();
              }}
            >
              <IconWrapper
                color={theme.colors.orange}
                height="14px"
                width="14px"
                cursor="pointer"
              >
                <ChatSvg />
              </IconWrapper>
              <T keyName="FeaturePaywall.ChatWithUs" defaultValue="Chat with us" />
            </FooterLink>

            {isUS && (
              <>
                <FooterDivider>|</FooterDivider>

                <FooterLink
                  as="a"
                  href="/curipod_teacher_pitch.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    Metrics.getLogger().logEvent('FeaturePaywall.DownloadOnePager', {
                      region: paywallRegion,
                    });
                  }}
                >
                  <IconWrapper
                    color={theme.colors.orange}
                    height="14px"
                    width="14px"
                    cursor="pointer"
                  >
                    <DownloadSvg />
                  </IconWrapper>
                  <T
                    keyName="FeaturePaywall.DownloadOnePager"
                    defaultValue="Download 1-pager for admin"
                  />
                </FooterLink>
              </>
            )}
          </FooterLinks>
        </CTAGroup>
      </MainContainer>
    </Modal>
  );
};
